import { motion } from "framer-motion";
import { Disc3, ArrowUpRight, Music2 } from "lucide-react";
import BackgroundWord from "./BackgroundWords";
import { useContent } from "../context/ContentContext";

const PLATFORMS = [
  { key: "spotify", label: "Spotify" },
  { key: "apple", label: "Apple Music" },
  { key: "amazon", label: "Amazon Music" },
  { key: "youtube", label: "YouTube" },
];

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export const Release = () => {
  const { content } = useContent();
  const releases = content?.releases || [];
  const meta = content?.release_meta || {};

  return (
    <section
      id="release"
      data-testid="release-section"
      className="relative bg-[var(--bg-section-alt,#030303)] py-32 md:py-44 overflow-hidden grain"
    >
      <BackgroundWord word="Releases" />

      <div className="absolute top-0 left-0 right-0 h-px hairline-red opacity-50" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
              {meta.eyebrow || "Now Playing — Featured Drops"}
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl max-w-3xl">
              {releases.length > 0 ? (
                <>
                  {meta.title_a || "Aktuelle"} <br />
                  <span className="text-glow-red text-white">{meta.title_b || "Releases."}</span>
                </>
              ) : (
                <>
                  {meta.empty_title_a || "Next Drop"} <br />
                  <span className="text-glow-red text-white">{meta.empty_title_b || "Loading."}</span>
                </>
              )}
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
            {releases.length > 0
              ? meta.intro || "Frisch aus dem Studio — überall streambar. Listen, share, repeat."
              : meta.empty_intro || "Wir arbeiten gerade an unseren ersten Releases. Stay tuned — die ersten Drops kommen bald."}
          </p>
        </div>

        {releases.length === 0 ? (
          <ComingSoon meta={meta} />
        ) : (
          <div className="grid gap-6 md:gap-8">
            {releases.map((r, i) => (
              <ReleaseCard key={r.id || `${r.title}-${i}`} r={r} i={i} />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin-slow { animation: spin-slow 18s linear infinite; }
      `}</style>
    </section>
  );
};

const ReleaseCard = ({ r, i }) => {
  const links = r.links || {};
  const hasAnyLink = PLATFORMS.some((p) => links[p.key]);

  return (
    <motion.div
      data-testid={`release-card-${i}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ delay: i * 0.08, duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
      className="grid md:grid-cols-12 gap-0 border border-white/[0.08] bg-[var(--bg-card,#080808)] overflow-hidden"
    >
      <div className="md:col-span-5 relative aspect-square md:aspect-auto overflow-hidden group">
        {r.cover ? (
          <img
            src={r.cover}
            alt={r.title || "Release Cover"}
            data-testid={`release-cover-${i}`}
            className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-1000"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0A0A0A] via-[#080808] to-black flex items-center justify-center">
            <Disc3 size={64} className="text-[#ff0033] opacity-40 animate-spin-slow" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#050505]/40 via-transparent to-[#ff0033]/[0.05] pointer-events-none" />
        <div className="absolute top-5 left-5 flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 px-3 py-2">
          <span className="red-led" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-white font-bold">
            {r.badge || "Out Now"}
          </span>
        </div>
      </div>

      <div className="md:col-span-7 p-10 md:p-14 flex flex-col justify-center">
        {r.artist && (
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#ff0033] font-bold mb-5">
            {r.artist}
          </div>
        )}
        <h3 className="font-display font-black uppercase text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[0.95]">
          {r.title || "Untitled"}
        </h3>

        {r.description && (
          <p className="mt-6 text-sm md:text-base text-zinc-400 max-w-xl leading-relaxed">
            {r.description}
          </p>
        )}

        <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm">
          {[
            { l: "Release", v: formatDate(r.release_date) || "—" },
            { l: "Format", v: r.format || "Single" },
            { l: "Label", v: r.label || "STRAßENWERT" },
          ].map((m) => (
            <div key={m.l} className="border-t border-white/10 pt-3">
              <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-600">{m.l}</div>
              <div className="font-display font-bold text-sm mt-1 text-white">{m.v}</div>
            </div>
          ))}
        </div>

        {hasAnyLink && (
          <div className="mt-8 pt-7 border-t border-white/[0.08]">
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold mb-4">
              Jetzt streamen
            </div>
            <div className="grid grid-cols-2 gap-3">
              {PLATFORMS.map((p) =>
                links[p.key] ? (
                  <a
                    key={p.key}
                    href={links[p.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`release-${i}-link-${p.key}`}
                    className="group flex items-center justify-between border border-white/[0.08] bg-[#050505] px-5 py-4 hover:border-[#ff0033]/40 hover:shadow-[0_0_24px_rgba(255,0,51,0.2)] transition-all duration-500"
                  >
                    <span className="font-display font-black uppercase tracking-tight text-base text-white">
                      {p.label}
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="text-[#ff0033] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300"
                    />
                  </a>
                ) : null
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const ComingSoon = ({ meta }) => (
  <motion.div
    data-testid="release-coming-soon"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
    className="relative grid md:grid-cols-12 gap-0 border border-white/[0.08] bg-[var(--bg-card,#080808)] overflow-hidden min-h-[420px] md:min-h-[480px]"
  >
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,0,51,0.08),transparent_70%)] pointer-events-none" />
    <div
      className="absolute inset-0 opacity-30 pointer-events-none"
      style={{
        backgroundImage:
          "repeating-linear-gradient(0deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, transparent 1px, transparent 6px)",
      }}
    />

    <div className="md:col-span-5 relative flex items-center justify-center p-12 border-r border-white/[0.06]">
      <div className="relative">
        <Disc3
          size={140}
          strokeWidth={1}
          className="text-[#ff0033] opacity-30 animate-spin-slow"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="red-led" />
        </div>
      </div>
    </div>

    <div className="md:col-span-7 p-10 md:p-14 flex flex-col justify-center relative">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#ff0033] font-bold mb-4">
        {meta.empty_badge || "Coming Soon"}
      </div>
      <h3 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
        {meta.empty_headline_a || "Wir arbeiten"} <br />
        <span className="text-glow-red text-white">{meta.empty_headline_b || "an der Musik."}</span>
      </h3>
      <p className="mt-6 text-sm md:text-base text-zinc-400 max-w-lg leading-relaxed">
        {meta.empty_body ||
          "Unsere ersten offiziellen Releases sind in der finalen Phase — Mix, Master, Visuals. Sobald der erste Track gedroppt ist, findest du ihn hier mit allen Streaming-Links."}
      </p>

      <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm">
        {[
          { l: "Status", v: meta.empty_status || "In Production" },
          { l: "ETA", v: meta.empty_eta || "TBA" },
          { l: "Label", v: "STRAßENWERT" },
        ].map((m) => (
          <div key={m.l} className="border-t border-white/10 pt-3">
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-600">{m.l}</div>
            <div className="font-display font-bold text-sm mt-1 text-white">{m.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase font-bold text-zinc-500">
        <Music2 size={14} className="text-[#ff0033]" />
        <span>Folg uns auf Instagram für Live-Updates</span>
      </div>
    </div>

    <div className="absolute bottom-0 left-0 right-0 h-px bg-[#ff0033] origin-left scale-x-[0.4] shadow-[0_0_12px_#ff0033]" />
  </motion.div>
);

export default Release;
